import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Download, ArrowLeft } from 'lucide-react';

const ImageViewer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [imageUrl, setImageUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Get the image URL from the query parameters
    const params = new URLSearchParams(location.search);
    const url = params.get('url');
    
    if (url) {
      try {
        const decodedUrl = decodeURIComponent(url);
        
        // Validate URL format
        if (!decodedUrl.startsWith('http')) {
          setError('Invalid image URL format');
          setIsLoading(false);
          return;
        }
        
        // Check if URL is for an image (basic check)
        const isImage = /\.(jpeg|jpg|gif|png|webp|bmp)$/i.test(decodedUrl);
        if (!isImage) {
          setError('URL does not appear to be an image');
          setIsLoading(false);
          return;
        }
        
        setImageUrl(decodedUrl);
        setIsLoading(false);
      } catch (e) {
        setError('Error processing the image URL');
        setIsLoading(false);
      }
    } else {
      setError('No image URL provided');
      setIsLoading(false);
    }
  }, [location.search]);

  const handleClose = () => {
    navigate(-1); // Go back to the previous page
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-700 dark:text-gray-300">Loading image...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="text-center p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <p className="text-red-500">{error}</p>
          <button 
            onClick={handleClose}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <div className="flex justify-between items-center p-4 bg-white dark:bg-gray-800 shadow-md">
        <h1 className="text-xl font-semibold text-gray-800 dark:text-white">Image Viewer</h1>
        <button 
          onClick={handleClose}
          className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          <X className="w-6 h-6 text-gray-600 dark:text-gray-300" />
        </button>
      </div>
      
      {/* Image Container */}
      <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
        <div className="max-w-full max-h-full">
          <img 
            src={imageUrl} 
            alt="Viewer" 
            className="max-w-full max-h-[calc(100vh-8rem)] object-contain rounded-lg shadow-lg"
          />
        </div>
      </div>
      
      {/* Footer */}
      <div className="p-4 bg-white dark:bg-gray-800 shadow-md">
        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            <button 
              onClick={handleClose}
              className="px-4 py-2 flex items-center gap-2 bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-white rounded hover:bg-gray-400 dark:hover:bg-gray-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <a 
              href={imageUrl} 
              download 
              className="px-4 py-2 flex items-center gap-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download Image
            </a>
          </div>
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-400 italic">
              Image from: {new URL(imageUrl).hostname}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageViewer;
